import {
  EXPECTED_CIRCUIT_ID,
  ProofFixture,
  Snapshot,
  proofFixture,
  report,
  snapshot,
} from './demoData';

export type Check = {
  label: string;
  detail: string;
  passed: boolean;
};

export async function runVerification(
  candidateSnapshot: Snapshot = snapshot,
  candidateReport: unknown = report,
  candidateProof: ProofFixture = proofFixture,
): Promise<Check[]> {
  const proofHash = await hashProof(candidateSnapshot, candidateProof);
  const reportHash = await sha256Hex(canonicalStringify(candidateReport));

  return [
    {
      label: 'Solvency inequality',
      detail: `${candidateSnapshot.assets} assets >= ${candidateSnapshot.liabilities} liabilities`,
      passed: BigInt(candidateSnapshot.assets) >= BigInt(candidateSnapshot.liabilities),
    },
    {
      label: 'Merkle root format',
      detail: `${candidateSnapshot.root_hash.slice(0, 10)}...${candidateSnapshot.root_hash.slice(-8)}`,
      passed: /^[0-9a-f]{64}$/.test(candidateSnapshot.root_hash),
    },
    {
      label: 'Proof artifact hash',
      detail: `${proofHash.slice(0, 10)}...${proofHash.slice(-8)}`,
      passed: proofHash === candidateSnapshot.proof_hash,
    },
    {
      label: 'Report artifact hash',
      detail: `${reportHash.slice(0, 10)}...${reportHash.slice(-8)}`,
      passed: reportHash === candidateSnapshot.report_hash,
    },
    {
      label: 'Circuit identifier',
      detail: `${candidateSnapshot.circuit_id.slice(0, 10)}...${candidateSnapshot.circuit_id.slice(-8)}`,
      passed: candidateSnapshot.circuit_id === EXPECTED_CIRCUIT_ID,
    },
  ];
}

export async function hashProof(
  candidateSnapshot: Snapshot,
  candidateProof: ProofFixture,
): Promise<string> {
  return sha256Hex(
    canonicalStringify({
      balance: candidateProof.balance,
      pathIndices: candidateProof.pathIndices,
      rootHash: BigInt(`0x${candidateSnapshot.root_hash}`).toString(10),
      siblingsHashes: candidateProof.siblingsHashes,
      siblingsSums: candidateProof.siblingsSums,
      username: candidateProof.username,
      usernameToBigInt:
        candidateProof.usernameToBigInt || parseUsername(candidateProof.username).toString(10),
    }),
  );
}

export function parseUsername(username: string): bigint {
  const bytes = new TextEncoder().encode(username);
  const hex = Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
  return BigInt(`0x${hex}`);
}

export function canonicalStringify(value: unknown): string {
  return JSON.stringify(toCanonicalJson(value));
}

function toCanonicalJson(value: unknown): unknown {
  if (typeof value === 'bigint') {
    return value.toString(10);
  }
  if (Array.isArray(value)) {
    return value.map((item) => toCanonicalJson(item));
  }
  if (value && typeof value === 'object') {
    const output: Record<string, unknown> = {};
    for (const key of Object.keys(value).sort()) {
      output[key] = toCanonicalJson((value as Record<string, unknown>)[key]);
    }
    return output;
  }
  return value;
}

export async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}
